import Phaser from 'phaser';
import { Transform, Sprite, Velocity, Collider, Entity, Keys } from '@badideagames/engine';
import { Game } from '../Game';
import { Flapper } from '../components/Flapper';
import { Gravity } from '../components/Gravity';
import { Pipe } from '../components/Pipe';

const WIDTH = 400;
const HEIGHT = 600;
const GROUND_Y = 540;
const PIPE_WIDTH = 52;
const PIPE_GAP = 140;
const PIPE_SPEED = -160;
const SPAWN_INTERVAL = 1.5;
const PLAYER_X = 80;
const TURD_SIZE = 30;
const GRAVITY_FORCE = 900;
const FLAP_FORCE = -320;

type State = 'ready' | 'playing' | 'dead';

export class MainScene extends Phaser.Scene {
  private ecs!: Game;
  private player!: Entity;
  private pipes: Entity[] = [];
  private state: State = 'ready';
  private score = 0;
  private bestScore = 0;
  private spawnTimer = 0;
  private bobTimer = 0;
  private deadTimer = 0;

  private scoreText!: Phaser.GameObjects.Text;
  private titleText!: Phaser.GameObjects.Text;
  private promptText!: Phaser.GameObjects.Text;
  private gameOverContainer!: Phaser.GameObjects.Container;
  private finalScoreText!: Phaser.GameObjects.Text;
  private bestScoreText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'MainScene' });
  }

  preload(): void {
    this.bestScore = parseInt(localStorage.getItem('flappy-turd-best') ?? '0', 10);
    this.createTextures();
  }

  private createTextures(): void {
    if (!this.textures.exists('turd')) {
      const g = this.make.graphics({ x: 0, y: 0 });
      // Base body
      g.fillStyle(0x8b6914);
      g.fillCircle(15, 21, 11);
      // Middle section
      g.fillStyle(0x7d5a0b);
      g.fillCircle(15, 13, 8);
      // Top peak
      g.fillStyle(0x6b4d0a);
      g.fillCircle(15, 6, 5);
      // Eyes
      g.fillStyle(0xffffff);
      g.fillCircle(11, 18, 3);
      g.fillCircle(19, 18, 3);
      g.fillStyle(0x222222);
      g.fillCircle(12, 18, 1.5);
      g.fillCircle(20, 18, 1.5);
      // Mouth
      g.lineStyle(1.5, 0x222222);
      g.beginPath();
      g.arc(15, 22, 4, 0.2, Math.PI - 0.2);
      g.strokePath();
      g.generateTexture('turd', TURD_SIZE, TURD_SIZE);
      g.destroy();
    }

    if (!this.textures.exists('pipe')) {
      const g = this.make.graphics({ x: 0, y: 0 });
      g.fillStyle(0x2e7d32);
      g.fillRect(0, 0, PIPE_WIDTH, HEIGHT);
      g.fillStyle(0x4caf50);
      g.fillRect(3, 0, 6, HEIGHT);
      g.fillStyle(0x1b5e20);
      g.fillRect(PIPE_WIDTH - 5, 0, 5, HEIGHT);
      g.generateTexture('pipe', PIPE_WIDTH, HEIGHT);
      g.destroy();
    }
  }

  create(): void {
    this.ecs = new Game(this);
    this.state = 'ready';
    this.score = 0;
    this.spawnTimer = 0;
    this.pipes = [];
    this.bobTimer = 0;
    this.deadTimer = 0;

    // Ground
    this.add
      .rectangle(WIDTH / 2, GROUND_Y + (HEIGHT - GROUND_Y) / 2, WIDTH, HEIGHT - GROUND_Y, 0x8b6914)
      .setDepth(15);
    this.add.rectangle(WIDTH / 2, GROUND_Y + 1, WIDTH, 3, 0x5d4e37).setDepth(15);
    this.add.rectangle(WIDTH / 2, GROUND_Y - 2, WIDTH, 5, 0x4caf50).setDepth(15);

    this.createPlayer();
    this.createUI();
  }

  private createPlayer(): void {
    this.player = this.ecs.world.createEntity();

    const transform = new Transform();
    transform.x = PLAYER_X;
    transform.y = HEIGHT / 2;
    this.player.add(transform);

    const sprite = new Sprite();
    sprite.texture = 'turd';
    sprite.depth = 10;
    this.player.add(sprite);

    this.player.add(new Velocity());

    const flapper = new Flapper();
    flapper.flapForce = FLAP_FORCE;
    flapper.alive = false;
    this.player.add(flapper);

    const gravity = new Gravity();
    gravity.force = 0;
    this.player.add(gravity);

    const collider = new Collider();
    collider.width = TURD_SIZE - 6;
    collider.height = TURD_SIZE - 6;
    collider.layer = 1;
    this.player.add(collider);
  }

  private createUI(): void {
    this.scoreText = this.add
      .text(WIDTH / 2, 50, '0', {
        fontSize: '48px',
        color: '#ffffff',
        fontFamily: 'Arial Black, Arial',
        stroke: '#000000',
        strokeThickness: 6,
      })
      .setOrigin(0.5)
      .setDepth(20)
      .setVisible(false);

    this.titleText = this.add
      .text(WIDTH / 2, 140, 'FLAPPY\nTURD', {
        fontSize: '52px',
        color: '#8B6914',
        fontFamily: 'Arial Black, Arial',
        stroke: '#000000',
        strokeThickness: 6,
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(20);

    this.promptText = this.add
      .text(WIDTH / 2, HEIGHT / 2 + 80, 'Tap or press Space', {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(20);

    this.finalScoreText = this.add
      .text(0, -30, '', {
        fontSize: '32px',
        color: '#ffffff',
        fontFamily: 'Arial Black, Arial',
        stroke: '#000000',
        strokeThickness: 4,
        align: 'center',
      })
      .setOrigin(0.5);

    this.bestScoreText = this.add
      .text(0, 20, '', {
        fontSize: '20px',
        color: '#FFD700',
        fontFamily: 'Arial',
        stroke: '#000000',
        strokeThickness: 3,
        align: 'center',
      })
      .setOrigin(0.5);

    const restartText = this.add
      .text(0, 70, 'Tap to restart', {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    this.gameOverContainer = this.add
      .container(WIDTH / 2, HEIGHT / 2, [this.finalScoreText, this.bestScoreText, restartText])
      .setDepth(25)
      .setVisible(false);
  }

  private spawnPipePair(): void {
    const minY = 80 + PIPE_GAP / 2;
    const maxY = GROUND_Y - 80 - PIPE_GAP / 2;
    const gapCenterY = Phaser.Math.Between(minY, maxY);

    // Top pipe
    const topPipe = this.ecs.world.createEntity();
    const topT = new Transform();
    topT.x = WIDTH + PIPE_WIDTH / 2;
    topT.y = gapCenterY - PIPE_GAP / 2 - HEIGHT / 2;
    topPipe.add(topT);
    const topS = new Sprite();
    topS.texture = 'pipe';
    topS.depth = 5;
    topPipe.add(topS);
    const topV = new Velocity();
    topV.vx = PIPE_SPEED;
    topPipe.add(topV);
    const topC = new Collider();
    topC.width = PIPE_WIDTH;
    topC.height = HEIGHT;
    topC.layer = 1;
    topPipe.add(topC);
    const pipeComp = new Pipe();
    pipeComp.gapY = gapCenterY;
    topPipe.add(pipeComp);

    // Bottom pipe
    const bottomPipe = this.ecs.world.createEntity();
    const botT = new Transform();
    botT.x = WIDTH + PIPE_WIDTH / 2;
    botT.y = gapCenterY + PIPE_GAP / 2 + HEIGHT / 2;
    bottomPipe.add(botT);
    const botS = new Sprite();
    botS.texture = 'pipe';
    botS.depth = 5;
    bottomPipe.add(botS);
    const botV = new Velocity();
    botV.vx = PIPE_SPEED;
    bottomPipe.add(botV);
    const botC = new Collider();
    botC.width = PIPE_WIDTH;
    botC.height = HEIGHT;
    botC.layer = 1;
    bottomPipe.add(botC);

    this.pipes.push(topPipe, bottomPipe);
  }

  private cleanupPipes(): void {
    this.pipes = this.pipes.filter((pipe) => {
      if (pipe.destroyed) return false;
      const t = pipe.get(Transform);
      if (t.x < -PIPE_WIDTH) {
        pipe.destroy();
        return false;
      }
      return true;
    });
  }

  private checkScore(): void {
    for (const pipe of this.pipes) {
      if (pipe.destroyed || !pipe.has(Pipe)) continue;
      const p = pipe.get(Pipe);
      if (p.scored) continue;
      const t = pipe.get(Transform);
      if (t.x + PIPE_WIDTH / 2 < PLAYER_X) {
        p.scored = true;
        this.score++;
        this.scoreText.setText(String(this.score));
      }
    }
  }

  private checkDeath(): boolean {
    const collisions = this.ecs.collisionSystem.getCollisions(this.player);
    if (collisions.length > 0) return true;

    const pt = this.player.get(Transform);
    if (pt.y + TURD_SIZE / 2 >= GROUND_Y) return true;
    if (pt.y - TURD_SIZE / 2 <= 0) return true;

    return false;
  }

  private die(): void {
    this.state = 'dead';
    this.deadTimer = 0;

    this.player.get(Flapper).alive = false;
    this.player.get(Velocity).vy = -200;

    for (const pipe of this.pipes) {
      if (!pipe.destroyed) pipe.get(Velocity).vx = 0;
    }

    if (this.score > this.bestScore) {
      this.bestScore = this.score;
      localStorage.setItem('flappy-turd-best', String(this.bestScore));
    }

    this.scoreText.setVisible(false);
    this.finalScoreText.setText(`Score: ${this.score}`);
    this.bestScoreText.setText(`Best: ${this.bestScore}`);
    this.gameOverContainer.setVisible(true);

    this.cameras.main.shake(200, 0.01);
  }

  private wantFlap(): boolean {
    return this.ecs.input.isKeyPressed(Keys.Space) || this.ecs.input.isMousePressed(0);
  }

  update(_time: number, delta: number): void {
    const dt = delta / 1000;
    // Check input BEFORE ecs.update() since it clears pressed state at the end
    const flap = this.wantFlap();

    switch (this.state) {
      case 'ready': {
        this.ecs.update(dt);

        this.bobTimer += dt;
        const pt = this.player.get(Transform);
        pt.y = HEIGHT / 2 + Math.sin(this.bobTimer * 3) * 8;

        this.promptText.setAlpha(0.7 + Math.sin(this.bobTimer * 4) * 0.3);

        if (flap) {
          this.state = 'playing';
          this.player.get(Flapper).alive = true;
          this.player.get(Gravity).force = GRAVITY_FORCE;
          this.player.get(Velocity).vy = FLAP_FORCE;
          this.titleText.setVisible(false);
          this.promptText.setVisible(false);
          this.scoreText.setVisible(true);
        }
        break;
      }

      case 'playing': {
        this.ecs.update(dt);

        this.spawnTimer += dt;
        if (this.spawnTimer >= SPAWN_INTERVAL) {
          this.spawnTimer -= SPAWN_INTERVAL;
          this.spawnPipePair();
        }

        const vel = this.player.get(Velocity);
        const transform = this.player.get(Transform);
        const targetRot = Phaser.Math.Clamp(vel.vy / 400, -0.5, 1.2);
        transform.rotation += (targetRot - transform.rotation) * 0.15;

        this.checkScore();
        this.cleanupPipes();

        if (this.checkDeath()) {
          this.die();
        }
        break;
      }

      case 'dead': {
        this.deadTimer += dt;
        this.ecs.update(dt);

        const pt = this.player.get(Transform);
        if (pt.y + TURD_SIZE / 2 >= GROUND_Y) {
          pt.y = GROUND_Y - TURD_SIZE / 2;
          this.player.get(Velocity).vy = 0;
          this.player.get(Gravity).force = 0;
        }

        if (this.player.get(Velocity).vy !== 0) {
          pt.rotation += 8 * dt;
        }

        if (this.deadTimer > 0.5 && flap) {
          this.ecs.destroy();
          this.scene.restart();
        }
        break;
      }
    }
  }
}
